import { Button } from '@/global/components/Button'
import IconButton from '@/global/components/Button/IconButton'
import { MonthCalendar } from '@/global/components/ui/month-calendar'
import { cn } from '@/lib/utils'
import dayjs from 'dayjs'
import {
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
} from 'lucide-react'
import { useState } from 'react'
import { DayPicker, type DayPickerProps } from 'react-day-picker'
import { ptBR } from 'react-day-picker/locale'

export type CalendarProps = DayPickerProps & { className?: string }

/**
 * Superfície comum das duas views. A largura é a do grid de dias (7 células de
 * 36px mais o `p-3`), e a altura mínima é a do mês de 6 semanas — sem ela,
 * trocar para o grid de meses, ou para um mês de 5 semanas, encolheria o
 * popover.
 */
const SUPERFICIE = 'w-[17.25rem] min-h-[21rem] p-3'

/** Troca de view: o grid novo entra em fade curto, sem mexer na caixa. */
const ENTRADA = 'animate-in fade-in-0 zoom-in-95 duration-150'

/**
 * Grid de dias. Substitui o `DateCalendar` do `@mui/x-date-pickers`: mesma
 * densidade (célula de 36px, semana começando no domingo, locale pt-BR) e
 * navegação por teclado vinda do react-day-picker.
 *
 * Fala `Date`, não `Dayjs` — a conversão é responsabilidade de quem chama
 * (`DateField` e `SelectPeriodo`).
 *
 * O CSS do pacote (`react-day-picker/style.css`) **não** é importado: todas as
 * classes abaixo substituem as do pacote, e o que sobraria dele seriam as
 * variáveis `--rdp-*` brigando com os tokens do tema. Por isso o `relative` no
 * root é obrigatório — é ele que ancora o `nav` absoluto; sem ele a navegação
 * escaparia para o canto do popover.
 */
export function Calendar({
    className,
    classNames,
    month,
    onMonthChange,
    ...props
}: CalendarProps) {
    // O mês exibido precisa ser controlado para o grid de meses conseguir
    // movê-lo; quando o call-site já controla, o dele manda.
    const [mesInterno, setMesInterno] = useState<Date | undefined>(
        () => month ?? props.defaultMonth,
    )
    const mesExibido = month ?? mesInterno
    const [escolhendoMes, setEscolhendoMes] = useState(false)

    function trocarMes(novoMes: Date) {
        setMesInterno(novoMes)
        onMonthChange?.(novoMes)
    }

    // Mesmo grid da aba Competência, para o gesto ser o mesmo nos dois lugares.
    if (escolhendoMes) {
        return (
            <div className={cn(SUPERFICIE, ENTRADA, className)}>
                <MonthCalendar
                    className="w-full p-0"
                    value={mesExibido ? dayjs(mesExibido) : dayjs()}
                    onChange={competencia => {
                        trocarMes(competencia.toDate())
                        setEscolhendoMes(false)
                    }}
                />
            </div>
        )
    }

    return (
        <DayPicker
            month={mesExibido}
            onMonthChange={trocarMes}
            locale={ptBR}
            showOutsideDays
            className={cn('relative', SUPERFICIE, ENTRADA, className)}
            components={{
                // O rótulo do mês abre o grid de meses, como no DatePicker
                // do MUI — clicar em "Setembro 2026" troca a view.
                CaptionLabel: ({ className: classe, children }) => (
                    <Button
                        type="button"
                        variant="text"
                        color="inherit"
                        aria-expanded={escolhendoMes}
                        endIcon={<ChevronDownIcon className="size-4" />}
                        className={cn(
                            'h-10 min-w-0 px-2 py-0',
                            'text-lg font-medium tracking-normal capitalize',
                            classe,
                        )}
                        onClick={() => setEscolhendoMes(true)}
                    >
                        {children}
                    </Button>
                ),
                Chevron: ({ orientation, className: classeSeta }) =>
                    orientation === 'left' ? (
                        <ChevronLeftIcon className={cn(classeSeta)} />
                    ) : (
                        <ChevronRightIcon className={cn(classeSeta)} />
                    ),
                PreviousMonthButton: ({
                    className: classe,
                    color: _cor,
                    ...props
                }) => (
                    <IconButton
                        color="default"
                        className={cn(classe)}
                        {...props}
                    />
                ),
                NextMonthButton: ({
                    className: classe,
                    color: _cor,
                    ...props
                }) => (
                    <IconButton
                        color="default"
                        className={cn(classe)}
                        {...props}
                    />
                ),
                DayButton: ({
                    day: _dia,
                    modifiers,
                    className: classe,
                    color: _cor,
                    ...props
                }) => (
                    <Button
                        variant={modifiers.selected ? 'contained' : 'text'}
                        color={modifiers.selected ? 'primary' : 'inherit'}
                        className={cn(
                            'size-9 min-w-0 rounded-full p-0',
                            'text-sm font-normal normal-case tracking-normal',
                            modifiers.outside && 'text-muted-foreground/50',
                            classe,
                        )}
                        {...props}
                    />
                ),
            }}
            classNames={{
                months: 'flex flex-col gap-4',
                month: '',
                month_caption: 'flex h-10 items-center justify-center',
                caption_label: '',
                // A faixa do `nav` cobre o cabeçalho inteiro: sem soltar o
                // clique aqui, ela engole o botão do mês que fica embaixo.
                nav: cn(
                    'absolute inset-x-3 top-3 flex items-center justify-between',
                    'pointer-events-none [&>button]:pointer-events-auto',
                ),
                button_previous: 'disabled:opacity-disabled',
                button_next: 'disabled:opacity-disabled',
                month_grid: 'mt-4 w-full border-collapse',
                // Domingo e sábado destacados só no rótulo, com as mesmas
                // cores do calendário da tabela de horários (`CalendarioMensal`).
                // O `Weekday` do react-day-picker não recebe a data, então a
                // mira é pela posição na linha — a semana começa no domingo.
                weekdays: cn(
                    'flex',
                    '[&>*:first-child]:text-error',
                    '[&>*:last-child]:text-[color-mix(in_oklab,_var(--error)_70%,_white)]',
                ),
                weekday:
                    'w-9 text-xs font-normal text-muted-foreground capitalize',
                week: 'mt-1 flex w-full',
                day: 'size-9 p-0 text-center text-sm',
                // O preenchimento do dia escolhido vem da variante do
                // `Button`, não daqui — aqui fica só o que é do calendário.
                today: '[&>button]:border [&>button]:border-primary',
                outside: 'text-muted-foreground/50',
                disabled: 'pointer-events-none text-muted-foreground/40',
                hidden: 'invisible',
                ...classNames,
            }}
            {...props}
        />
    )
}
